import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@xingternal/button';
import { trackEvent } from '@xingternal/hops-tracking';
import {
  SingleSizeIllustrationFeedbackActiveHappy,
  SingleSizeIllustrationFeedbackActiveNeutral,
  SingleSizeIllustrationFeedbackActiveSad,
} from '@xingternal/illustrations';

import { useLoginStates } from '@/hooks';

import * as S from './feedback.styles';

type FeedbackButton = 'very_good' | 'okay' | 'not_good';

const feedbackSurveyByRatingLoggedIn = {
  very_good: 'https://feedback.xing.com/s/1777444/hMK87X-multi-134787',
  okay: 'https://feedback.xing.com/s/1777444/hMK87X-multi-134788',
  not_good: 'https://feedback.xing.com/s/1777444/hMK87X-multi-134789',
};

const feedbackSurveyByRatingLoggedOut = {
  very_good: 'https://feedback.xing.com/s/1813207/80r55s-multi-139488',
  okay: 'https://feedback.xing.com/s/1813207/80r55s-multi-139489',
  not_good: 'https://feedback.xing.com/s/1813207/80r55s-multi-139490',
};

const onFeedbackClick = (button: FeedbackButton, isLoggedIn: boolean): void => {
  const surveyUrl = isLoggedIn ? feedbackSurveyByRatingLoggedIn[button] : feedbackSurveyByRatingLoggedOut[button];

  trackEvent('PropTrackAction', {
    PropTrackAction: `jobs_apply_feedback_${button}`,
    PropActionOrigin: 'jobs_apply_feedback_instant_apply',
  });

  surveyUrl && window?.open(surveyUrl, '_blank', 'noopener');
};

export const Feedback: React.FC = () => {
  const { isLoggedIn } = useLoginStates();

  return (
    <S.Banner display="inline">
      <S.Headline size="large" sizeConfined="xlarge">
        <FormattedMessage id="JOBS_ONE_CLICK_APPLY_FEEDBACK_HEADLINE" />
      </S.Headline>
      <S.BodyCopy size="medium">
        <FormattedMessage id="JOBS_ONE_CLICK_APPLY_FEEDBACK_SUB_HEADLINE" />
      </S.BodyCopy>
      <S.ButtonsWrapper>
        <S.FeedbackButtonWrapper>
          <S.FeedbackIconButton
            data-testid="feedback-icon-button-very-good"
            onClick={() => onFeedbackClick('very_good', isLoggedIn)}
          >
            <SingleSizeIllustrationFeedbackActiveHappy width={42} height={42} />
          </S.FeedbackIconButton>
          <Button variant="secondary" size="small" onClick={() => onFeedbackClick('very_good', isLoggedIn)}>
            <FormattedMessage id="JOBS_ONE_CLICK_APPLY_FEEDBACK_BUTTON_POSITIVE" />
          </Button>
        </S.FeedbackButtonWrapper>
        <S.FeedbackButtonWrapper>
          <S.FeedbackIconButton
            data-testid="feedback-icon-button-okay"
            onClick={() => onFeedbackClick('okay', isLoggedIn)}
          >
            <SingleSizeIllustrationFeedbackActiveNeutral width={42} height={42} />
          </S.FeedbackIconButton>
          <Button variant="secondary" size="small" onClick={() => onFeedbackClick('okay', isLoggedIn)}>
            <FormattedMessage id="JOBS_ONE_CLICK_APPLY_FEEDBACK_BUTTON_NEUTRAL" />
          </Button>
        </S.FeedbackButtonWrapper>
        <S.FeedbackButtonWrapper>
          <S.FeedbackIconButton
            data-testid="feedback-icon-button-not-good"
            onClick={() => onFeedbackClick('not_good', isLoggedIn)}
          >
            <SingleSizeIllustrationFeedbackActiveSad width={42} height={42} />
          </S.FeedbackIconButton>
          <Button variant="secondary" size="small" onClick={() => onFeedbackClick('not_good', isLoggedIn)}>
            <FormattedMessage id="JOBS_ONE_CLICK_APPLY_FEEDBACK_BUTTON_NEGATIVE" />
          </Button>
        </S.FeedbackButtonWrapper>
      </S.ButtonsWrapper>
    </S.Banner>
  );
};
